import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import CommunityIQLogo from "@/components/CommunityIQLogo";
import { Radio, Trophy, Users, CheckCircle2, Clock, Lock } from "lucide-react";

const POLL_MS = 3000;

function fmtCloses(iso) {
  if (!iso) return null;
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

export default function BroadcastVote() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const timer = useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const load = async () => {
    try {
      const r = await fetch(`${API}/api/public/elections/${id}`);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setData(await r.json());
      setErr(null);
    } catch (e) {
      setErr(e.message);
    }
  };

  useEffect(() => {
    load();
    timer.current = setInterval(load, POLL_MS);
    return () => clearInterval(timer.current);
  }, [id]);

  if (err && !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-center px-6" data-testid="broadcast-error">
        <div>
          <div className="text-6xl mb-4">🗳️</div>
          <h1 className="text-3xl font-semibold mb-2">Election not available</h1>
          <p className="text-muted-foreground">The broadcast link is invalid, expired, or the election hasn't opened yet.</p>
        </div>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground" data-testid="broadcast-loading">
        Loading live tally…
      </div>
    );
  }

  const leading = data.leading_option_index;
  const total = data.total_votes || 0;
  const closesAt = fmtCloses(data.closes_at);

  return (
    <div
      className="min-h-screen w-full bg-background text-foreground px-6 lg:px-16 py-8 lg:py-12 flex flex-col"
      data-testid="broadcast-page"
    >
      {/* Top bar */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
        <CommunityIQLogo className="h-14 w-auto" />
        <div className="flex items-center gap-3">
          {data.is_open ? (
            <div className="inline-flex items-center gap-2 rounded-full border border-emerald-500/60 bg-emerald-500/10 px-4 py-2 text-emerald-300 font-semibold" data-testid="broadcast-live-badge">
              <Radio className="w-4 h-4 animate-pulse" />
              LIVE · results updating every {POLL_MS / 1000} s
            </div>
          ) : (
            <div className="inline-flex items-center gap-2 rounded-full border border-border bg-accent/50 px-4 py-2 text-muted-foreground font-semibold" data-testid="broadcast-closed-badge">
              <Lock className="w-4 h-4" />
              Election closed · final tally
            </div>
          )}
        </div>
      </div>

      {/* Question hero */}
      <div className="mb-8 lg:mb-12">
        <div className="text-lg text-gold uppercase tracking-widest mb-2">Election night · CommunityIQ</div>
        <h1 className="text-4xl lg:text-6xl font-semibold tracking-tight leading-[1.05] mb-3" data-testid="broadcast-question">
          {data.question}
        </h1>
        {data.description && (
          <p className="text-lg lg:text-xl text-foreground/75 max-w-4xl leading-relaxed">
            {data.description}
          </p>
        )}
      </div>

      {/* Stat strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <div className="hoa-card p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Votes cast</div>
          <div className="text-4xl font-mono text-gold" data-testid="broadcast-total-votes">{total}</div>
          <div className="text-xs text-muted-foreground mt-1">of {data.owner_count} eligible</div>
        </div>
        <div className={`hoa-card p-5 ${data.quorum_reached ? "royal-ring" : ""}`}>
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Quorum</div>
          <div className={`text-4xl font-mono ${data.quorum_reached ? "text-emerald-300" : "text-foreground/95"}`} data-testid="broadcast-quorum">
            {total}/{data.quorum_needed}
          </div>
          <div className="text-xs text-muted-foreground mt-1">
            {data.quorum_reached ? "Reached ✓" : `Needs ${data.quorum_needed} for ${data.quorum_pct}%`}
          </div>
        </div>
        <div className="hoa-card p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Eligible voters</div>
          <div className="text-4xl font-mono text-foreground/95 flex items-center gap-2">
            <Users className="w-6 h-6 text-primary" />
            {data.owner_count}
          </div>
        </div>
        <div className="hoa-card p-5">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Closes</div>
          <div className="text-lg font-mono text-foreground/95 flex items-center gap-2 mt-2">
            <Clock className="w-5 h-5 text-primary" />
            {closesAt || "When board closes"}
          </div>
        </div>
      </div>

      {/* Live tally bars */}
      <div className="flex-1 space-y-4" data-testid="broadcast-tally">
        {data.options.map((opt, i) => {
          const count = data.tally[i] || 0;
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          const isLead = leading === i && total > 0;
          return (
            <div
              key={`bcast-${i}`}
              className={`relative overflow-hidden rounded-2xl border transition-colors ${isLead ? "border-gold" : "border-border"}`}
              data-testid={`broadcast-row-${i}`}
            >
              <div
                className={`absolute inset-y-0 left-0 transition-all duration-700 ease-out ${isLead ? "bg-gold/25" : "bg-primary/20"}`}
                style={{ width: `${pct}%` }}
              />
              <div className="relative flex items-center justify-between px-6 lg:px-8 py-5 lg:py-6">
                <div className="flex items-center gap-4 min-w-0">
                  {isLead && <Trophy className="w-6 h-6 lg:w-7 lg:h-7 text-gold flex-shrink-0" />}
                  <span className={`text-2xl lg:text-3xl font-semibold truncate ${isLead ? "text-foreground" : "text-foreground/90"}`}>
                    {opt}
                  </span>
                </div>
                <div className="text-right flex-shrink-0 ml-4">
                  <div className={`font-mono text-4xl lg:text-5xl ${isLead ? "text-gold" : "text-foreground/95"}`} data-testid={`broadcast-pct-${i}`}>
                    {pct}%
                  </div>
                  <div className="font-mono text-sm text-muted-foreground">{count} votes</div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Footer */}
      <div className="mt-10 pt-6 border-t border-border flex items-center justify-between text-sm text-muted-foreground flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-4 h-4 text-gold" />
          Every ballot verified against the owner registry. Broadcast is read-only.
        </div>
        <div className="font-mono">Opened by {data.created_by || "Board"} · {new Date(data.created_at).toLocaleDateString()}</div>
      </div>
    </div>
  );
}
